/**
 * Logger utility for client_react
 * Provides consistent logging format and log levels similar to rag-api and client
 */

// Log levels
export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

// Current log level - can be configured
let currentLogLevel = LogLevel.DEBUG;

// Set environment-specific log level
if (process.env.NODE_ENV === 'production') {
  currentLogLevel = LogLevel.INFO;
} else if (process.env.REACT_APP_IS_DOCKER === 'true') {
  // In Docker development, set to INFO level by default
  // This can be adjusted based on your needs
  currentLogLevel = LogLevel.DEBUG;
}

// For browser environments, we're using console logging
// In Docker/production, these logs are captured by the container logging system
// This is just a note to document the logging approach

// Logger class
class Logger {
  private context: string;

  constructor(context: string) {
    this.context = context;
  }

  // Format log message with timestamp and context
  private formatMessage(message: string): string {
    const timestamp = new Date().toISOString();
    return `[${timestamp}] [${this.context}]: ${message}`;
  }

  // Debug level logs
  debug(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.DEBUG) {
      const formattedMessage = this.formatMessage(message);
      console.debug(formattedMessage, ...args);
    }
  }

  // Info level logs
  info(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.INFO) {
      const formattedMessage = this.formatMessage(message);
      console.info(formattedMessage, ...args);
    }
  }

  // Warning level logs
  warn(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.WARN) {
      const formattedMessage = this.formatMessage(message);
      console.warn(formattedMessage, ...args);
    }
  }

  // Error level logs
  error(message: string, ...args: any[]): void {
    if (currentLogLevel <= LogLevel.ERROR) {
      const formattedMessage = this.formatMessage(message);
      console.error(formattedMessage, ...args);
    }
  }
}

// Set log level
export const setLogLevel = (level: LogLevel): void => {
  currentLogLevel = level;
};

// Get current log level
export const getLogLevel = (): LogLevel => {
  return currentLogLevel;
};

// Create a logger instance
export const createLogger = (context: string): Logger => {
  return new Logger(context);
};

// Default logger
export default createLogger('App');
